import { useGetHealth } from '@agos/api-client/health'
import { useGetSystemLogs } from '@agos/api-client/reporting'
import { T, tt } from '@agos/shared'
import {
  DataTable,
  FilterBar,
  FilterSelect,
  PageHeader,
  Spinner,
  StatCard,
  StatusBadge,
  TabGroup,
  cn,
  type Column,
} from '@agos/ui'
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  Database,
  Info,
  Receipt,
  RefreshCw,
  ScrollText,
  Server,
} from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'wouter'

import { AuditLogContent } from './AuditLogPage'
import {
  EMPTY_SYSTEM_FILTER,
  SYSTEM_LOG_LIMIT,
  SYSTEM_LOG_MAX_LIMIT,
  SYSTEM_LOG_REACHES,
  formatCount,
  formatStamp,
  formatUptime,
  groupKey,
  groupLogs,
  healthColour,
  levelColor,
  levelCounts,
  matchesLevel,
  matchesSystemSearch,
  mayHaveMore,
  normaliseLevel,
  shortSpelledWarnings,
  sourceNames,
  systemQuery,
  type Health,
  type LogGroup,
  type SystemFilter,
  type SystemListing,
  type SystemLogRow,
} from './log-viewer'

/**
 * `/system-logs` — what the system was doing, and what broke.
 *
 * **FB-107.** Legacy shows the newest 200 rows in the order they arrived. On the restored data the
 * newest thousand are **926 information lines, 59 errors and 15 warnings**, and they are only **57
 * distinct messages** — 259 of them *"Fiskálny doklad vytlačený – Rozvoz"* with a different order
 * number, 211 the same for card-online, 153 a card payment on the restaurant till. A page called *what
 * broke* that shows a receipt printing four hundred times is not answering its own question. Grouped by
 * message shape with errors first, the fifty-seven are readable and the recurring failure is at the top;
 * the newest occurrence of each keeps the raw line.
 *
 * **FB-108.** `/system-logs` takes `level`, `source`, `deviceId`, `since` and `limit` — no offset, no
 * total. It cannot be paged: a bigger `limit` is the only way to see further back. Legacy pages its
 * sibling `/audit-logs` (79 700 rows, with a total) and neither page says which it is doing. Both say so
 * here.
 *
 * **FB-110.** And the reach is a cliff, not a clamp: `limit=1000` answers a thousand rows back to
 * 14 July, `limit=1001` answers **two hundred** back to 27 July only. Legacy Express does the same
 * (`limitRaw <= 1000 ? limitRaw : 200`), so the choices stop at the ceiling and the page names it.
 *
 * **FB-111.** `level` is matched against `info`, `warning`, `error` and nothing else; any other value
 * is silently dropped and the whole log comes back instead. So there is no *Debug* choice — it would
 * show everything and call itself a filter — and the *Varovania* choice says what it cannot reach:
 * 81 rows are recorded as `warn`, 10 374 rows deep, so neither the level filter (wrong spelling) nor
 * the unfiltered list (past the 1 000 ceiling) will show them. The source filter will: they are all
 * `circuit-breaker`, which has 121 rows in total.
 *
 * **§5 boundary.** Legacy's tab strip is *system · kasa · audit*, and the kasa tab is
 * `KasaLogsContent` imported from `pages/kasa-logs`. `manager-reporting` may not import
 * `manager-kasa`, so the kasa tab is a link to `/kasa-logs` rather than an embed. Same two clicks,
 * one module boundary intact — recorded in `docs/pages/manager-reporting/system-logs.md`.
 *
 * Ported from legacy `pages/system-logs.tsx` (372 lines).
 */

type LogTab = 'system' | 'audit'

export function SystemLogsPage() {
  const [tab, setTab] = useState<LogTab>('system')

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Logy')}
        description={tt('Systémové logy a audit záznamov')}
        icon={<ScrollText className="w-5 h-5" />}
      />

      <div className="flex flex-wrap items-end gap-2">
        <TabGroup
          variant="bar"
          className="mb-0 flex-1"
          tabs={[
            { id: 'system', label: tt('Systémové logy'), icon: ScrollText },
            { id: 'audit', label: tt('Audit log'), icon: ClipboardCheck },
          ]}
          active={tab}
          onChange={(id) => setTab(id as LogTab)}
        />
        {/* Legacy embeds the kasa log here; §5 forbids reaching into `manager-kasa`, so it is a link. */}
        <Link
          href="/kasa-logs"
          data-kasa-link
          className="flex items-center gap-2 px-3 py-2 mb-1 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground border border-border"
        >
          <Receipt className="w-4 h-4" />
          <T>Kasa logy</T>
        </Link>
      </div>

      {tab === 'system' ? <SystemLogsTab /> : <AuditLogContent />}
    </div>
  )
}

function SystemLogsTab() {
  const [filter, setFilter] = useState<SystemFilter>(EMPTY_SYSTEM_FILTER)
  const [limit, setLimit] = useState(SYSTEM_LOG_LIMIT)
  const [grouped, setGrouped] = useState(true)
  const [openShape, setOpenShape] = useState<string | null>(null)

  const {
    data: healthData,
    isFetching: healthFetching,
    refetch: refetchHealth,
  } = useGetHealth({
    // Legacy re-checks every 60 s with a `setInterval` it also re-creates on every filter change.
    query: { refetchInterval: 60_000 },
  })
  const health = (healthData ?? {}) as Health

  const {
    data: logData,
    isLoading,
    isFetching,
    refetch,
  } = useGetSystemLogs(systemQuery(filter, limit, openShape !== null))
  const rows = useMemo(() => ((logData ?? {}) as SystemListing).rows ?? [], [logData])

  const shown = useMemo(
    () =>
      rows.filter(
        (row) => matchesLevel(row, filter.level) && matchesSystemSearch(row, filter.search),
      ),
    [rows, filter.level, filter.search],
  )
  const counts = levelCounts(shown)
  const groups = useMemo(() => groupLogs(shown), [shown])
  const sources = useMemo(() => sourceNames(rows), [rows])
  const truncated = mayHaveMore(rows, limit)
  // FB-111: the server's whitelist matches `warning` only, so the short spelling is unreachable there.
  const shortWarnings = shortSpelledWarnings(rows)

  const set = <K extends keyof SystemFilter>(key: K, value: SystemFilter[K]) =>
    setFilter((current) => ({ ...current, [key]: value }))

  const ok = health.status === 'ok' && health.db === 'ok'
  const known = health.status !== undefined

  const rowColumns: Column<SystemLogRow>[] = useMemo(
    () => [
      {
        key: 'level',
        header: tt('Úroveň'),
        sortable: true,
        sortValue: (row) => normaliseLevel(row.level),
        render: (row) => (
          <StatusBadge label={(row.level ?? '').toUpperCase()} color={levelColor(row.level)} />
        ),
      },
      {
        key: 'message',
        header: tt('Správa'),
        sortable: true,
        sortValue: (row) => row.message ?? '',
        render: (row) => <LogMessage row={row} />,
      },
      {
        key: 'source',
        header: tt('Zdroj'),
        sortable: true,
        sortValue: (row) => row.source ?? '',
        render: (row) => (
          <span className="text-xs text-muted-foreground font-mono">{row.source ?? '-'}</span>
        ),
      },
      {
        key: 'createdAt',
        header: tt('Čas'),
        sortable: true,
        sortValue: (row) => row.createdAt ?? '',
        render: (row) => (
          <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums">
            {formatStamp(row.createdAt)}
          </span>
        ),
      },
    ],
    [],
  )

  return (
    <div className="space-y-5">
      {/* ─── is it up ─── */}
      <div
        className={cn(
          'rounded-2xl border p-5',
          ok
            ? 'border-emerald-500/30 bg-emerald-500/5'
            : known
              ? 'border-red-500/30 bg-red-500/5'
              : 'border-border bg-card',
        )}
        data-health
        data-health-state={known ? (ok ? 'ok' : 'degraded') : 'unknown'}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {!known ? (
              <Spinner />
            ) : ok ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-500" />
            )}
            <span className="font-semibold text-sm">
              <T>Stav systému</T>
            </span>
            <StatusBadge
              label={known ? (ok ? tt('OK') : tt('Problém')) : tt('Neznámy')}
              color={healthColour(health)}
            />
          </div>
          <button
            type="button"
            onClick={() => void refetchHealth()}
            disabled={healthFetching}
            aria-label={tt('Obnoviť stav systému')}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-muted hover:bg-accent disabled:opacity-50"
          >
            <RefreshCw className={cn('w-3.5 h-3.5', healthFetching && 'animate-spin')} />
            <T>Obnoviť</T>
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <HealthCell
            icon={<Server className="w-4 h-4 text-muted-foreground shrink-0" />}
            label={tt('API server')}
            value={known ? (health.status === 'ok' ? tt('Beží') : tt('Problém')) : '-'}
            good={health.status === 'ok'}
            known={known}
          />
          <HealthCell
            icon={<Database className="w-4 h-4 text-muted-foreground shrink-0" />}
            label={tt('Databáza')}
            value={known ? (health.db === 'ok' ? tt('Online') : tt('Nedostupná')) : '-'}
            good={health.db === 'ok'}
            known={known}
          />
          <HealthCell
            icon={<Clock className="w-4 h-4 text-muted-foreground shrink-0" />}
            label={tt('V prevádzke')}
            value={health.uptime === undefined ? '-' : formatUptime(health.uptime)}
            good
            known={health.uptime !== undefined}
          />
        </div>

        {health.timestamp !== undefined && (
          <p className="text-xs text-muted-foreground mt-3" data-health-checked>
            <T>Posledná kontrola</T>: {formatStamp(health.timestamp)}
          </p>
        )}
      </div>

      {/* ─── how much of this is the thing you came for ─── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-level-counts>
        <StatCard
          label={tt('Chyby')}
          value={formatCount(counts.error)}
          icon={AlertTriangle}
          color={counts.error > 0 ? 'red' : 'emerald'}
        />
        <StatCard
          label={tt('Varovania')}
          value={formatCount(counts.warning)}
          icon={AlertTriangle}
          color="amber"
        />
        <StatCard label={tt('Info')} value={formatCount(counts.info)} icon={Info} color="blue" />
        <StatCard
          label={tt('Rôznych správ')}
          value={formatCount(groups.length)}
          sub={`${tt('z')} ${formatCount(counts.total)} ${tt('riadkov')}`}
          icon={ScrollText}
          color="primary"
        />
      </div>

      <FilterBar
        search={{
          value: filter.search,
          onChange: (value) => set('search', value),
          placeholder: tt('Hľadať v logoch...'),
        }}
        filters={
          <>
            <FilterSelect
              value={filter.level}
              label={tt('Úroveň')}
              onChange={(value) => set('level', value)}
              // Exactly the three the server matches. A fourth choice would return the whole log
              // and call itself a filter (FB-111).
              options={[
                { value: 'all', label: tt('Všetky úrovne') },
                { value: 'error', label: tt('Chyby') },
                { value: 'warning', label: tt('Varovania') },
                { value: 'info', label: tt('Info') },
              ]}
            />
            <FilterSelect
              value={filter.source}
              label={tt('Zdroj')}
              onChange={(value) => set('source', value)}
              options={[
                { value: 'all', label: tt('Všetky zdroje') },
                ...sources.map((source) => ({ value: source, label: source })),
              ]}
            />
            <input
              type="date"
              value={filter.since}
              onChange={(event) => set('since', event.target.value)}
              aria-label={tt('Od dátumu')}
              className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
            />
            <FilterSelect
              value={grouped ? 'grouped' : 'flat'}
              label={tt('Zobrazenie')}
              onChange={(value) => setGrouped(value === 'grouped')}
              options={[
                { value: 'grouped', label: tt('Zoskupené podľa správy') },
                { value: 'flat', label: tt('Chronologicky') },
              ]}
            />
          </>
        }
        actions={
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            aria-label={tt('Obnoviť logy')}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-muted hover:bg-accent disabled:opacity-50"
          >
            <RefreshCw className={cn('w-3.5 h-3.5', isFetching && 'animate-spin')} />
            <T>Obnoviť</T>
          </button>
        }
      />

      {/* FB-108: no offset, no total - the reach is the only control there is, and it is stated.
          FB-110: and it stops at 1 000, because 1 001 gets answered with 200. */}
      <div className="flex flex-wrap items-center gap-2 text-[11px]" data-reach>
        <span className="text-muted-foreground">
          <T>Server neponúka stránkovanie ani celkový počet</T> - <T>toto je posledných</T>{' '}
          <strong>{formatCount(limit)}</strong> <T>záznamov</T>
          {truncated && (
            <>
              {' '}
              <span className="text-amber-500 font-semibold" data-truncated>
                <T>a starších je viac</T>
              </span>
            </>
          )}
          {limit >= SYSTEM_LOG_MAX_LIMIT && (
            <>
              {' '}
              · <T>viac ako</T> {formatCount(SYSTEM_LOG_MAX_LIMIT)}{' '}
              <T>server neposkytne - nad týmto počtom vráti svojich 200</T>
            </>
          )}
        </span>
        <span className="flex-1" />
        {SYSTEM_LOG_REACHES.map((size) => (
          <button
            key={size}
            type="button"
            onClick={() => setLimit(size)}
            aria-pressed={limit === size}
            className={cn(
              'px-2.5 py-1 rounded-lg border font-semibold',
              limit === size
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border text-muted-foreground hover:text-foreground',
            )}
          >
            {formatCount(size)}
          </button>
        ))}
      </div>

      {/* FB-111. Two different true statements, each shown where it is the true one: filtered, the
          server has already excluded the short spelling; unfiltered, the page can count it. */}
      {filter.level === 'warning' && (
        <p className="text-[11px] text-amber-500" data-warn-spelling="filtered">
          <T>
            Serverový filter varovaní matchuje len `warning`. Riadky zapísané ako `warn` v tomto
            zozname nie sú a hlbšie ako 1 000 riadkov sa nedostanete - nájdete ich cez filter zdroja.
          </T>
        </p>
      )}
      {filter.level === 'all' && shortWarnings > 0 && (
        <p className="text-[11px] text-muted-foreground" data-warn-spelling="counted">
          {formatCount(shortWarnings)}{' '}
          <T>
            z načítaných riadkov je zapísaných ako `warn` namiesto `warning`. Počítajú sa tu ako
            varovania; serverový filter ich nenájde.
          </T>
        </p>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : grouped ? (
        <GroupedLogs groups={groups} open={openShape} onToggle={setOpenShape} />
      ) : (
        <div data-log-rows>
          <DataTable<SystemLogRow>
            columns={rowColumns}
            data={shown}
            rowKey={(row) => row.id ?? 0}
            pageSize={25}
            emptyMessage={tt('Žiadne záznamy')}
            emptyIcon={<CheckCircle2 className="w-10 h-10 mb-3 opacity-30" />}
          />
        </div>
      )}
    </div>
  )
}

function HealthCell({
  icon,
  label,
  value,
  good,
  known,
}: {
  icon: ReactNode
  label: string
  value: string
  good: boolean
  known: boolean
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-background/50 border border-border/50 px-4 py-3">
      {icon}
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p
          className={cn(
            'text-sm font-semibold truncate',
            !known ? 'text-muted-foreground' : good ? 'text-emerald-500' : 'text-red-500',
          )}
        >
          {value}
        </p>
      </div>
    </div>
  )
}

/**
 * The sixty-one, worst first.
 *
 * A group is one message shape: the numbers in it differ, the sentence does not. The count says how
 * often, the span says over what stretch, and the newest occurrence is there in full — so nothing is
 * hidden, only stopped from repeating four hundred times above the errors.
 */
function GroupedLogs({
  groups,
  open,
  onToggle,
}: {
  groups: LogGroup[]
  open: string | null
  onToggle: (shape: string | null) => void
}) {
  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <CheckCircle2 className="w-10 h-10 mb-3 opacity-30" />
        <T>Žiadne záznamy</T>
      </div>
    )
  }

  return (
    <div className="space-y-2" data-log-groups>
      {groups.map((group) => {
        const key = groupKey(group)
        const expanded = open === key
        return (
          <div
            key={key}
            data-log-group
            data-level={group.level}
            /* §9.654 - which source this group is, so a narrowing can be asserted as *every group is the one
               chosen* rather than as *fewer groups than before*. `system_logs` grows whenever anything errors,
               so a count taken before and compared after is a race against the table - and it lost one, with
               7 groups where the earlier read saw 5. */
            data-log-source={group.source ?? ''}
            className={cn(
              'rounded-xl border bg-card',
              group.level === 'error'
                ? 'border-red-500/30'
                : group.level === 'warning'
                  ? 'border-amber-500/30'
                  : 'border-border',
            )}
          >
            <button
              type="button"
              onClick={() => onToggle(expanded ? null : key)}
              className="w-full flex items-start gap-3 p-3 text-left"
            >
              <StatusBadge label={group.level.toUpperCase()} color={levelColor(group.level)} />
              <div className="min-w-0 flex-1">
                {/* §9.747 - the request a server error answered, so two endpoints failing alike read as two. */}
                {group.request !== null && (
                  <p className="font-mono text-[11px] text-muted-foreground break-all" data-group-request>
                    {group.request}
                  </p>
                )}
                <p className="text-sm break-words">{group.shape}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {group.source ?? tt('bez zdroja')} ·{' '}
                  {group.count > 1 ? (
                    <>
                      <strong data-group-count>{formatCount(group.count)}×</strong> <T>od</T>{' '}
                      {formatStamp(stampOf(group.firstSeen))} <T>do</T>{' '}
                      {formatStamp(stampOf(group.lastSeen))}
                    </>
                  ) : (
                    formatStamp(stampOf(group.lastSeen))
                  )}
                </p>
              </div>
            </button>

            {expanded && (
              <div className="px-3 pb-3 border-t border-border/60 pt-2" data-group-newest>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">
                  <T>Najnovší výskyt</T>
                </p>
                <LogMessage row={group.newest} />
                <p className="text-[11px] text-muted-foreground mt-1 tabular-nums">
                  {formatStamp(group.newest.createdAt)}
                  {group.newest.deviceId !== null && group.newest.deviceId !== undefined && (
                    <> · {group.newest.deviceId}</>
                  )}
                </p>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

/** A group keeps epoch milliseconds; `formatStamp` takes what the API sends. */
function stampOf(stamp: number | null): string | null {
  return stamp === null ? null : new Date(stamp).toISOString()
}

function LogMessage({ row }: { row: SystemLogRow }) {
  const context = row.context ?? {}
  return (
    <div className="min-w-0">
      <p className="text-sm break-words">{row.message ?? '-'}</p>
      {Object.keys(context).length > 0 && (
        <pre className="text-[11px] text-muted-foreground mt-1 font-mono bg-muted/50 rounded p-2 overflow-x-auto">
          {JSON.stringify(context, null, 2)}
        </pre>
      )}
    </div>
  )
}
